import {
  Box,
  Edge,
  type Node,
  type Rect,
  type NodeOrigin,
} from '@xyflow/react';
import {
  boxToRect,
  rectToBox,
  getNodePositionWithOrigin,
} from '@xyflow/system';
import { IVlanItem, GlobalIpSubnetType } from 'src/context/global/types';
import {
  IPort,
  EDataType,
  IpInterface,
  EDataSwitchType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import {
  AppNode,
  NodeModel,
} from 'src/modules/network-configuration/models/node-model';
import {
  nodeScale,
  generateNodeId,
} from 'src/modules/network-configuration/models/node-utils';

import { dispatchNodesChanged } from './dispatch-nodes-changed';
import {
  isV4Format,
  incrementIp,
  isValidHostAddress,
  convertSubnetMaskToLength,
} from './ip';
import { cleanName } from './string';

// we have to make sure that parent nodes are rendered before their children
export const sortNodes = (a: Node, b: Node): number => {
  if (!a.parentId && b.parentId) return -1;
  if (a.parentId && !b.parentId) return 1;

  if (a.type === b.type) return 0;
  return a.type === EDataType.Group && b.type !== EDataType.Group ? -1 : 1;
};

export const getId = (prefix = 'node') => `${prefix}_${Math.random() * 10000}`;

export const getNodePositionInsideParent = (
  node: Partial<Node>,
  groupNode: Node,
) => {
  const position = node.position ?? { x: 0, y: 0 };
  const nodeWidth = node.measured?.width ?? 0;
  const nodeHeight = node.measured?.height ?? 0;
  const groupWidth = groupNode.measured?.width ?? 0;
  const groupHeight = groupNode.measured?.height ?? 0;

  if (position.x < groupNode.position.x) {
    position.x = 0;
  } else if (position.x + nodeWidth > groupNode.position.x + groupWidth) {
    position.x = groupWidth - nodeWidth;
  } else {
    position.x -= groupNode.position.x;
  }

  if (position.y < groupNode.position.y) {
    position.y = 0;
  } else if (position.y + nodeHeight > groupNode.position.y + groupHeight) {
    position.y = groupHeight - nodeHeight;
  } else {
    position.y -= groupNode.position.y;
  }

  return position;
};

export const getBoundsOfBoxes = (box1: Box, box2: Box): Box => ({
  x: Math.min(box1.x, box2.x),
  y: Math.min(box1.y, box2.y),
  x2: Math.max(box1.x2, box2.x2),
  y2: Math.max(box1.y2, box2.y2),
});

export const getRelativeNodesBounds = (
  nodes: Node[],
  nodeOrigin: NodeOrigin = [0, 0],
): Rect => {
  if (nodes.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const box = nodes.reduce(
    (currBox, node) => {
      const { x, y } = getNodePositionWithOrigin(node, nodeOrigin);
      return getBoundsOfBoxes(
        currBox,
        rectToBox({
          x,
          y,
          width: node.width || 0,
          height: node.height || 0,
        }),
      );
    },
    { x: Infinity, y: Infinity, x2: -Infinity, y2: -Infinity },
  );

  return boxToRect(box);
};

interface NodeObject {
  id: string;
  width: number;
  height: number;
  positionX: number;
  positionY: number;
  parentId: string;
}

export const getInsideNodeId = (node: Node, nodes: Node[]): string => {
  // convert nodes to NodeObject
  if (!nodes || nodes.length === 0) {
    return '';
  }

  return nodes[0].id;

  // TODO: fix group inside group

  const nodesObject: NodeObject[] = nodes.map((n) => {
    let positionX = n.position?.x ?? 0;
    let positionY = n.position?.y ?? 0;
    if (n.parentId) {
      const parentNode = nodes.find((_n) => _n.id === n.parentId);
      if (parentNode) {
        positionX += parentNode.position?.x ?? 0;
        positionY += parentNode.position?.y ?? 0;
      }
    }
    return {
      id: n.id,
      width: n.width ?? 0,
      height: n.height ?? 0,
      positionX,
      positionY,
      parentId: n.parentId ?? '',
    };
  });

  const intersectionInsideNode = nodesObject.filter(
    (n) =>
      n.parentId !== node.id &&
      (node.position?.x ?? Infinity) > (n.positionX ?? Infinity) &&
      (node.position?.y ?? Infinity) > (n.positionY ?? Infinity) &&
      // check inside node
      node.position.x + (node.width ?? 0) < n.positionX + (n.width ?? 0) &&
      node.position.y + (node.height ?? 0) < n.positionY + (n.height ?? 0),
  );

  const intersectionChild = intersectionInsideNode.reduce<NodeObject>(
    (minObj, obj) =>
      (obj?.width ?? Infinity) < (minObj?.width ?? Infinity) ? obj : minObj,
    {
      width: Infinity,
      height: Infinity,
      id: '',
      positionX: Infinity,
      positionY: Infinity,
      parentId: '',
    },
  );
  return intersectionChild.id;
};

export const getNodeName = (nodes: Node[], currentName: string): string => {
  const isExist = nodes.find(
    (node) =>
      (node.data?.category as NodeCategoryModel)?.name === currentName ||
      (node.data?.category as NodeCategoryModel)?.name === `${currentName} 1`,
  );
  if (isExist) {
    const existingNames = nodes.map(
      (node) => (node.data?.category as NodeCategoryModel)?.name,
    );
    if (
      existingNames.includes(currentName) ||
      existingNames.includes(`${currentName} 1`)
    ) {
      let i = 1;
      while (existingNames.includes(`${currentName} ${i}`)) {
        i += 1;
      }
      currentName = `${currentName} ${i}`;
    }
    return currentName;
  }
  return `${currentName} 1`;
};

export const getNodeIpWithSubnet = (
  nodes: Node[],
  globalIpSubnet: GlobalIpSubnetType | undefined,
): string | undefined => {
  if (!globalIpSubnet || !globalIpSubnet.ipSubnet) return undefined;

  const allIps = nodes
    .map((node) => (node.data?.category as NodeCategoryModel)?.ip)
    .filter((ip): ip is string => !!ip);

  const subnet = globalIpSubnet.ipSubnet;
  const { subnetMask } = globalIpSubnet;

  // If subnet mask is not provided, we can't validate boundaries
  // Fall back to old behavior but with iteration limit
  if (!subnetMask) {
    let ip = incrementIp(subnet);
    let iterations = 0;
    const maxIterations = 1000; // Safety limit for unvalidated case

    while (ip && allIps.includes(ip) && iterations < maxIterations) {
      ip = incrementIp(ip);
      iterations += 1;
    }

    return ip && iterations < maxIterations ? ip : undefined;
  }

  // Calculate subnet size to set maximum iteration limit
  const subnetMaskLength = convertSubnetMaskToLength(subnetMask);
  if (subnetMaskLength === 0) {
    // Invalid subnet mask
    return undefined;
  }

  // Calculate maximum number of host addresses (excluding network and broadcast)
  // For a /24 subnet: 2^8 - 2 = 254
  const maxHosts = 2 ** (32 - subnetMaskLength) - 2;
  const maxIterations = Math.min(maxHosts, 10000); // Cap at 10000 for safety

  // Start from the first host address (network + 1)
  let ip = incrementIp(subnet, 1, { subnet, subnetMask });
  let iterations = 0;

  // Try to find an available IP within the subnet
  while (ip && iterations < maxIterations) {
    // Check if this IP is already used
    if (!allIps.includes(ip)) {
      // Verify it's still a valid host address (should always be true, but double-check)
      if (isValidHostAddress(ip, subnet, subnetMask)) {
        return ip;
      }
    }

    // Try next IP
    const nextIp = incrementIp(ip, 1, { subnet, subnetMask });
    if (!nextIp) {
      // Next IP would be outside subnet (likely broadcast address)
      break;
    }
    ip = nextIp;
    iterations += 1;
  }

  // Subnet exhausted or iteration limit reached
  return undefined;
};

export const getEdgeConnectedPort = (
  edges: Edge[],
  nodeId: string,
  portId: string,
): Edge | undefined =>
  edges.find(
    (edge) =>
      edge?.sourceHandle === `${nodeId}__${portId}__source` ||
      edge?.targetHandle === `${nodeId}__${portId}__target`,
  );

export const getInfoConnected = (
  currentNodeId: string,
  toPortId: string,
  edges: Edge[],
  nodes: Node[],
): {
  node: Node | undefined;
  port: IPort | undefined;
  edge: Edge | undefined;
} => {
  const edgeConnected = getEdgeConnectedPort(edges, currentNodeId, toPortId);
  if (!edgeConnected)
    return { node: undefined, port: undefined, edge: undefined };
  const connectedNode = nodes.find(
    (node) =>
      (node.id === edgeConnected?.source ||
        node.id === edgeConnected?.target) &&
      node.id !== currentNodeId,
  );
  if (!connectedNode)
    return { node: undefined, port: undefined, edge: edgeConnected };
  const connectedNodePort = (
    connectedNode?.data?.category as NodeCategoryModel
  )?.ports?.find(
    (port) =>
      `${connectedNode?.id}__${port.id}__source` ===
        edgeConnected?.sourceHandle ||
      `${connectedNode?.id}__${port.id}__target` ===
        edgeConnected?.targetHandle,
  );
  return {
    node: connectedNode,
    port: connectedNodePort,
    edge: edgeConnected,
  };
};

export const getEdgeConnected = (
  fromNodeId: string,
  toNodeId: string,
  edges: Edge[],
): Edge | undefined => {
  const edgeConnected = edges.find(
    (edge) =>
      (edge?.source === fromNodeId && edge?.target === toNodeId) ||
      (edge?.target === fromNodeId && edge?.source === toNodeId),
  );
  if (!edgeConnected) return undefined;
  return edgeConnected;
};

export const getEdgeInfo = (
  edge: Edge,
  nodes: Node[],
): {
  sourceNode: Node | undefined;
  sourcePort: string | undefined;
  targetNode: Node | undefined;
  targetPort: string | undefined;
} => {
  const sourceNode = nodes.find((node) => node.id === edge.source);
  const targetNode = nodes.find((node) => node.id === edge.target);
  const sourcePort = edge.sourceHandle
    ?.replace(`${sourceNode?.id}__`, '')
    .replace('__source', '') as string;

  const targetPort = edge.targetHandle
    ?.replace(`${targetNode?.id}__`, '')
    .replace('__target', '') as string;
  return { sourceNode, sourcePort, targetNode, targetPort };
};

export const getPortConnected = (nodeId: string, edge: Edge) => {
  if (edge.source === nodeId) {
    return edge.sourceHandle
      ?.replace(`${nodeId}__`, '')
      .replace('__source', '');
  }
  return edge.targetHandle?.replace(`${nodeId}__`, '').replace('__target', '');
};

export const getPortIndexConnected = (
  nodeId: string,
  edge: Edge,
  nodeCategory: NodeCategoryModel,
) => {
  const portId = getPortConnected(nodeId, edge);
  if (!portId) return undefined;
  return nodeCategory?.portIndexes[portId];
};

export const getAllNodeConnected = (
  nodeId: string,
  edges: Edge[],
  nodes: Node[],
): Node[] => {
  const edgesConnected = edges.filter(
    (edge) => edge.source === nodeId || edge.target === nodeId,
  );
  const nodeConnected = nodes.filter((node) =>
    edgesConnected.some(
      (edge) => edge.source === node.id || edge.target === node.id,
    ),
  );
  return nodeConnected.filter((node) => node.id !== nodeId);
};

export const getNewDuplicateNode = (
  targetNode: Node<NodeModel>,
  nodes: Node[],
  positionX: number,
  positionY: number,
) => {
  const nodeData = targetNode.data?.category as NodeCategoryModel;
  // deep copy node data
  const newNodeData = JSON.parse(JSON.stringify(nodeData));
  let currentName = getNodeName(nodes, cleanName(newNodeData?.name));
  const isExistName = nodes.find(
    (node) => (node.data?.category as NodeCategoryModel)?.name === currentName,
  );
  if (isExistName) {
    const existingNames = nodes.map(
      (node) => (node.data?.category as NodeCategoryModel)?.name,
    );
    if (existingNames.includes(currentName)) {
      let i = 1;
      while (existingNames.includes(`${currentName} ${i}`)) {
        i += 1;
      }
      currentName = `${currentName} ${i}`;
    }
  }

  let currentIp = newNodeData?.ip;

  if (currentIp && isV4Format(currentIp)) {
    const existingIps = nodes.map(
      (node) => (node.data?.category as NodeCategoryModel)?.ip,
    );
    if (existingIps.includes(currentIp)) {
      let i = 1;
      while (existingIps.includes(incrementIp(currentIp, i))) {
        i += 1;
      }
      currentIp = incrementIp(currentIp, i);
    }
  }

  // increment each interface ip
  if (newNodeData?.interfaces) {
    newNodeData.interfaces = [];
  }

  const id = `${targetNode.type}-${generateNodeId()}`;

  const newNode: AppNode = {
    id,
    position: {
      x: positionX,
      y: positionY,
    },
    type: targetNode.type as NodeModel['key'],
    ...(newNodeData.resizable && {
      width: newNodeData.viewSize.width * nodeScale,
      height: newNodeData.viewSize.height * nodeScale,
    }),
    //
    data: {
      key: targetNode.type as NodeModel['key'],
      label: targetNode.data.label,
      category: {
        ...targetNode.data.category,
        ...newNodeData,
        name: currentName,
        ip: currentIp,
      },
    },
  };

  return newNode;
};

export type SyncNodeIpInterfacesParams = {
  newEdges: Edge[];
  setNodes: (payload: Node[] | ((nodes: Node[]) => Node[])) => void;
  vlans: IVlanItem[];
  /** When edge added/updated: sync interfaces for nodes of this edge */
  edgeId?: string;
  /** When edge removed: sync interfaces for nodes of this edge */
  edgeRemoved?: Edge;
};

function getAffectedNodeIds(
  newEdges: Edge[],
  edgeId?: string,
  edgeRemoved?: Edge,
): string[] {
  const ids: string[] = [];
  if (edgeRemoved) {
    ids.push(edgeRemoved.source, edgeRemoved.target);
  }
  if (edgeId) {
    const edge = newEdges.find((e) => e.id === edgeId);
    if (edge) ids.push(edge.source, edge.target);
  }
  return [...new Set(ids)];
}

/**
 * Syncs ipInterfaces for nodes affected by edge add/remove (from VLANs on edges),
 * then validates and stores isValid + validationErrors in node.data.category.
 * Replaces validateNodeIpInterface and validateNodeAfterRemoveEdge.
 */
export const syncNodeIpInterfacesAndValidate = (
  params: SyncNodeIpInterfacesParams,
) => {
  const { newEdges, setNodes, vlans, edgeId, edgeRemoved } = params;
  const affectedIds = getAffectedNodeIds(newEdges, edgeId, edgeRemoved);

  setNodes((nodes) => {
    const allIpInterfaces: IpInterface[] = nodes
      .map((node: any) => node.data?.category?.ipInterfaces || [])
      .flat();
    const newIps: string[] = [];

    const newNodes = nodes.map((node: any) => {
      if (!affectedIds.includes(node.id)) return { ...node };

      const ipInterfaces: IpInterface[] =
        node.data?.category?.ipInterfaces || [];
      const otherEdges = newEdges.filter(
        (e) => e.target === node.id || e.source === node.id,
      );
      const allVlanIds = otherEdges
        .map((item: any) => item?.data?.vlanIds || [])
        .flat();
      const uniqueVlanIds = new Set(allVlanIds);
      const vlanIds = Array.from(uniqueVlanIds).map((v) =>
        parseInt(v.toString(), 10),
      );

      let newIpInterfaces: IpInterface[] = [];
      const isManaged =
        node.data?.category?.switchType?.includes(EDataSwitchType.MANAGED) ||
        node?.data?.category?.type === EDataType.Device;

      if (isManaged) {
        newIpInterfaces = ipInterfaces.filter((i: any) =>
          vlanIds.includes(parseInt(i.vlanId.toString(), 10)),
        );

        const isDevice = node?.data?.category?.type === EDataType.Device;
        const rememberedDhcp =
          isDevice &&
          (node.data?.category?.dhcp ||
            (
              node.data?.category?.ipInterfaces as IpInterface[] | undefined
            )?.some((ip) => ip.dhcp));

        vlanIds.forEach((vlanId) => {
          if (newIpInterfaces.some((u: any) => u.vlanId === vlanId)) return;
          const vlan = vlans?.find(
            (c) => c.vlanId.toString() === vlanId.toString(),
          );
          let newIp: string | undefined;
          if (vlan?.ipSubnet) {
            const otherSameVlan = allIpInterfaces
              .filter((u: any) => u.vlanId === vlanId)
              .map((u: any) => u.ip);
            newIp = incrementIp(vlan.ipSubnet);
            if ([...otherSameVlan, ...newIps].includes(newIp)) {
              let i = 1;
              while (
                [...otherSameVlan, ...newIps].includes(
                  incrementIp(newIp || '', i),
                )
              ) {
                i += 1;
              }
              newIp = incrementIp(newIp || '', i);
            }
            newIps.push(newIp || '');
          }
          const newInterface: IpInterface = {
            vlanId,
            ip: newIp || '',
            subnet: vlan?.ipSubnet || '',
            subnetMask: vlan?.subnetMask || '',
          };
          if (isDevice) newInterface.dhcp = !!rememberedDhcp;
          newIpInterfaces.push(newInterface);
        });

        newIpInterfaces.sort((a, b) => a.vlanId - b.vlanId);
      }

      const newCategory = {
        ...node.data?.category,
        ipInterfaces: newIpInterfaces,
      };

      if (node?.data?.category?.type === EDataType.Device) {
        const hadDhcp = (
          node.data?.category?.ipInterfaces as IpInterface[] | undefined
        )?.some((ip) => ip.dhcp);
        const hasDhcpRemaining = newIpInterfaces.some((ip) => ip.dhcp);
        if (hadDhcp || hasDhcpRemaining) newCategory.dhcp = true;
      }

      return {
        ...node,
        data: { ...node.data, category: newCategory },
      };
    });

    return newNodes;
  });
  dispatchNodesChanged(affectedIds);
};

/** @deprecated Use syncNodeIpInterfacesAndValidate({ edgeId, newEdges, setNodes, vlans }) */
export const validateNodeIpInterface = (
  id: string,
  newEdges: Edge[],
  setNodes: (payload: Node[] | ((nodes: Node[]) => Node[])) => void,
  vlans: IVlanItem[],
) =>
  syncNodeIpInterfacesAndValidate({
    edgeId: id,
    newEdges,
    setNodes,
    vlans,
  });

/** @deprecated Use syncNodeIpInterfacesAndValidate({ edgeRemoved, newEdges, setNodes, vlans }) */
export const validateNodeAfterRemoveEdge = (
  edgeRemoved: Edge,
  newEdges: Edge[],
  setNodes: (payload: Node[] | ((nodes: Node[]) => Node[])) => void,
  vlans: IVlanItem[],
) =>
  syncNodeIpInterfacesAndValidate({
    edgeRemoved,
    newEdges,
    setNodes,
    vlans,
  });

export const getEdgeName = (id: string, nodes: Node[]) => {
  const [source, target] = id.split('____');
  if (!source || !target) return '';
  const [sourceId, sourcePort] = source.split('__');
  const [targetId, targetPort] = target.split('__');
  const sourceNode = nodes.find((node) => node.id === sourceId);
  const targetNode = nodes.find((node) => node.id === targetId);
  const isSourceSwitch =
    (sourceNode?.data?.category as NodeCategoryModel)?.type ===
    EDataType.Switch;
  const isTargetSwitch =
    (targetNode?.data?.category as NodeCategoryModel)?.type ===
    EDataType.Switch;
  const sourceName = getPortName(
    (sourceNode?.data?.category as NodeCategoryModel)?.name,
    sourcePort,
    isSourceSwitch,
  );
  const targetName = getPortName(
    (targetNode?.data?.category as NodeCategoryModel)?.name,
    targetPort,
    isTargetSwitch,
  );
  return `${targetName} <-> ${sourceName}`;
};

const getPortName = (name: string, port: string, isSwitch: boolean) =>
  isSwitch ? `${name} ${port ? `(${port})` : ''}` : name;

export const getPortNameFromHandle = (handle: string) => {
  const parts = handle.split('__');
  const port = parts.length >= 3 ? parts[1] : '';
  return port;
};
